const normalizeValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value?.toDate === 'function') {
    return value.toDate().toISOString();
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (Array.isArray(value)) {
    return JSON.stringify(value);
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

const formatCsvValue = (value) => {
  const normalized = normalizeValue(value);
  const escaped = normalized.replace(/"/g, '""');

  if (/[",\n]/.test(escaped)) {
    return `"${escaped}"`;
  }

  return escaped;
};

export const convertRecordsToCsv = (records) => {
  if (!records || records.length === 0) {
    return '';
  }

  const headers = Array.from(
    records.reduce((set, record) => {
      Object.keys(record || {}).forEach((key) => set.add(key));
      return set;
    }, new Set())
  );

  const rows = records.map((record) =>
    headers.map((header) => formatCsvValue(record?.[header])).join(',')
  );

  return [headers.join(','), ...rows].join('\n');
};

export const triggerCsvDownload = (filename, csvContent) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(url), 0);
};

const parseCsvRows = (csvContent) => {
  const rows = [];
  let row = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < csvContent.length; i += 1) {
    const char = csvContent[i];
    const nextChar = csvContent[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (!inQuotes && (char === ',' || char === '\n' || char === '\r')) {
      if (char === '\r' && nextChar === '\n') {
        i += 1;
      }
      row.push(current);
      current = '';
      if (char === '\n' || char === '\r') {
        rows.push(row);
        row = [];
      }
      continue;
    }

    current += char;
  }

  row.push(current);
  if (row.length > 1 || row[0] !== '') {
    rows.push(row);
  }

  return rows;
};

const normalizeImportedValue = (value) => {
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  if (value === 'null') {
    return null;
  }
  if (value.startsWith('{') || value.startsWith('[')) {
    try {
      return JSON.parse(value);
    } catch (error) {
      return value;
    }
  }
  return value;
};

export const parseCsvToRecords = (csvContent) => {
  if (!csvContent) {
    return [];
  }

  const rows = parseCsvRows(csvContent);
  if (rows.length === 0) {
    return [];
  }

  const [headerRow, ...dataRows] = rows;
  const headers = headerRow.map((header) => header.trim());

  return dataRows
    .filter((row) => row.some((value) => value !== ''))
    .map((row) => {
      const record = {};
      headers.forEach((header, index) => {
        record[header] = normalizeImportedValue(row[index] ?? '');
      });
      return record;
    });
};

const createCrcTable = () => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[i] = c >>> 0;
  }
  return table;
};

const CRC_TABLE = createCrcTable();

const crc32 = (data) => {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i += 1) {
    crc = CRC_TABLE[(crc ^ data[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const concatUint8Arrays = (arrays) => {
  const totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  arrays.forEach((arr) => {
    result.set(arr, offset);
    offset += arr.length;
  });
  return result;
};

export const createZipBlob = (files) => {
  const encoder = new TextEncoder();
  const localParts = [];
  const centralParts = [];
  let offset = 0;

  files.forEach(({ filename, data }) => {
    const nameBytes = encoder.encode(filename);
    const dataBytes = data instanceof Uint8Array
      ? data
      : new Uint8Array(data || 0);
    const checksum = crc32(dataBytes);

    const localHeader = new Uint8Array(30 + nameBytes.length);
    const localView = new DataView(localHeader.buffer);
    localView.setUint32(0, 0x04034b50, true);
    localView.setUint16(4, 20, true);
    localView.setUint16(6, 0, true);
    localView.setUint16(8, 0, true);
    localView.setUint16(10, 0, true);
    localView.setUint16(12, 0, true);
    localView.setUint32(14, checksum, true);
    localView.setUint32(18, dataBytes.length, true);
    localView.setUint32(22, dataBytes.length, true);
    localView.setUint16(26, nameBytes.length, true);
    localView.setUint16(28, 0, true);
    localHeader.set(nameBytes, 30);

    localParts.push(localHeader, dataBytes);

    const centralHeader = new Uint8Array(46 + nameBytes.length);
    const centralView = new DataView(centralHeader.buffer);
    centralView.setUint32(0, 0x02014b50, true);
    centralView.setUint16(4, 20, true);
    centralView.setUint16(6, 20, true);
    centralView.setUint16(8, 0, true);
    centralView.setUint16(10, 0, true);
    centralView.setUint16(12, 0, true);
    centralView.setUint16(14, 0, true);
    centralView.setUint32(16, checksum, true);
    centralView.setUint32(20, dataBytes.length, true);
    centralView.setUint32(24, dataBytes.length, true);
    centralView.setUint16(28, nameBytes.length, true);
    centralView.setUint16(30, 0, true);
    centralView.setUint16(32, 0, true);
    centralView.setUint16(34, 0, true);
    centralView.setUint16(36, 0, true);
    centralView.setUint32(38, 0, true);
    centralView.setUint32(42, offset, true);
    centralHeader.set(nameBytes, 46);

    centralParts.push(centralHeader);
    offset += localHeader.length + dataBytes.length;
  });

  const centralDirectory = concatUint8Arrays(centralParts);
  const endRecord = new Uint8Array(22);
  const endView = new DataView(endRecord.buffer);
  endView.setUint32(0, 0x06054b50, true);
  endView.setUint16(4, 0, true);
  endView.setUint16(6, 0, true);
  endView.setUint16(8, centralParts.length, true);
  endView.setUint16(10, centralParts.length, true);
  endView.setUint32(12, centralDirectory.length, true);
  endView.setUint32(16, offset, true);
  endView.setUint16(20, 0, true);

  const zipBytes = concatUint8Arrays([...localParts, centralDirectory, endRecord]);
  return new Blob([zipBytes], { type: 'application/zip' });
};

export const createCsvZipBlob = (files) => {
  const encoder = new TextEncoder();
  return createZipBlob(
    files.map(({ filename, content }) => ({
      filename,
      data: encoder.encode(content ?? ''),
    }))
  );
};

export const triggerFileDownload = (filename, blob) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(url), 0);
};

export const triggerZipDownload = (filename, zipBlob) => {
  triggerFileDownload(filename, zipBlob);
};

export const extractZipFiles = async (zipBlob) => {
  const buffer = await zipBlob.arrayBuffer();
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);
  const decoder = new TextDecoder();

  const findEndRecord = () => {
    for (let i = bytes.length - 22; i >= 0 && i >= bytes.length - 65557; i -= 1) {
      if (view.getUint32(i, true) === 0x06054b50) {
        return i;
      }
    }
    return -1;
  };

  const endOffset = findEndRecord();
  if (endOffset < 0) {
    throw new Error('Invalid ZIP file.');
  }

  const totalEntries = view.getUint16(endOffset + 10, true);
  const centralDirOffset = view.getUint32(endOffset + 16, true);
  if (totalEntries > 10000 || centralDirOffset >= bytes.length) {
    throw new Error('Invalid ZIP directory.');
  }

  const files = [];
  let cursor = centralDirOffset;

  for (let entryIndex = 0; entryIndex < totalEntries; entryIndex += 1) {
    if (cursor + 46 > bytes.length) {
      throw new Error('Invalid ZIP directory entry.');
    }
    if (view.getUint32(cursor, true) !== 0x02014b50) {
      throw new Error('Invalid ZIP directory entry.');
    }
    const compression = view.getUint16(cursor + 10, true);
    const compressedSize = view.getUint32(cursor + 20, true);
    const fileNameLength = view.getUint16(cursor + 28, true);
    const extraLength = view.getUint16(cursor + 30, true);
    const commentLength = view.getUint16(cursor + 32, true);
    const localHeaderOffset = view.getUint32(cursor + 42, true);
    const nameStart = cursor + 46;
    const nameEnd = nameStart + fileNameLength;
    if (nameEnd + extraLength + commentLength > bytes.length) {
      throw new Error('Invalid ZIP filename entry.');
    }
    const fileName = decoder.decode(bytes.slice(nameStart, nameEnd));

    if (localHeaderOffset + 30 > bytes.length) {
      throw new Error('Invalid ZIP local header.');
    }
    const localSignature = view.getUint32(localHeaderOffset, true);
    if (localSignature !== 0x04034b50) {
      throw new Error('Invalid ZIP local header.');
    }
    const localNameLength = view.getUint16(localHeaderOffset + 26, true);
    const localExtraLength = view.getUint16(localHeaderOffset + 28, true);
    const dataStart = localHeaderOffset + 30 + localNameLength + localExtraLength;

    if (compression !== 0) {
      throw new Error('Unsupported ZIP compression.');
    }
    if (dataStart + compressedSize > bytes.length) {
      throw new Error('Invalid ZIP file data.');
    }

    const fileData = bytes.slice(dataStart, dataStart + compressedSize);
    files.push({ filename: fileName, data: fileData });

    cursor += 46 + fileNameLength + extraLength + commentLength;
  }

  return files;
};

export const extractZipCsvFiles = async (zipBlob) => {
  const files = await extractZipFiles(zipBlob);
  return extractCsvFiles(files);
};

export const extractCsvFiles = (files) => {
  const decoder = new TextDecoder();
  return files
    .filter((file) => file.filename.toLowerCase().endsWith('.csv'))
    .map((file) => ({
      filename: file.filename,
      content: decoder.decode(file.data),
    }));
};
